import { useContext, useRef, useState } from 'react';
import { L } from '@lang';
import { Accordion, classNames, DisplayTitle, Link, Paragraph, SimpleCell } from '@vkontakte/vkui';
import { Icon28CancelOutline, Icon28LinkCircleOutline, Icon28QrCodeOutline } from '@vkontakte/icons';
import { FormatWikiContent } from '@dui';
import { QrCode } from '@dui/QrCode';
import { TitleImage } from '../TitleImage';
import { AppContext } from '../../contexts';
import { ReactComponent as DatingIcon } from '../../assets/dating_icon.svg';
import { reachGoal } from '../../helpers';
import { VkDatingLogo } from '../VkDatingLogo';
import { CustomDisplayTitle } from '../CustomDisplayTitle';
import styles from './StoreBlock.module.css';

const STORE_BLOCK_ID = 'store';

interface StoreBlockProps {
  getRootRef?: React.Ref<HTMLDivElement>;
}

const sendGoal = (name: string) => reachGoal(`contest_store_click_${name.replace(' ', '_').toLowerCase()}`);

export function StoreBlock(props: StoreBlockProps) {
  const { getRootRef } = props;
  const { deviceType, storesData } = useContext(AppContext);
  const isMobile = deviceType === 'mobile';
  const [openedItems, setOpenedItems] = useState<number[]>([]);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const isTouchRef = useRef(false);

  // сбрасываем hover, если был touch
  const handleTouchStart = () => {
    isTouchRef.current = true;
    setHoveredIndex(null);
  };

  const toggleAccordion = (index: number) => {
    setOpenedItems((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]));
  };

  const handleMouseEnter = (index: number) => {
    if (!isTouchRef.current) setHoveredIndex(index);
  };

  const handleMouseLeave = () => {
    if (!isTouchRef.current) setHoveredIndex(null);
  };

  return (
    <div id={STORE_BLOCK_ID} className={classNames(styles.root, styles[`root__${deviceType}`])}>
      <div ref={getRootRef} />
      <div className={styles.header}>
        <VkDatingLogo deviceType={deviceType} />
        <TitleImage type="download" deviceType={deviceType} className={styles.wrapperTitle} />
        <CustomDisplayTitle weight="2" level="4" className={styles.wrapperText} Component="h3">
          {L.t('crush_landing_store_subtitle')}
        </CustomDisplayTitle>
      </div>
      <div className={styles.stores}>
        {isMobile ? (
          <>
            {storesData.map(({ link, name }) => (
              <SimpleCell
                onClick={() => sendGoal(name)}
                className={styles.cell}
                key={name}
                href={link}
                target="_blank"
                rel="noreferrer noopener"
                after={<Icon28LinkCircleOutline />}
              >
                <DisplayTitle level="4">{name}</DisplayTitle>
              </SimpleCell>
            ))}
          </>
        ) : (
          <>
            {storesData.map(({ link, name }, index) => {
              const isOpenedAccordion = openedItems.includes(index);

              return (
                <Accordion key={link} onChange={() => toggleAccordion(index)}>
                  <div
                    onMouseEnter={() => handleMouseEnter(index)}
                    onMouseLeave={handleMouseLeave}
                    onTouchStart={handleTouchStart}
                    className={classNames(
                      styles.summary,
                      isOpenedAccordion && styles.summary__opened,
                      (hoveredIndex === index || hoveredIndex === index - 1) && styles.summary__hover,
                    )}
                  >
                    <Accordion.Summary
                      ExpandIcon={Icon28QrCodeOutline}
                      CollapseIcon={Icon28CancelOutline}
                      hasActive={!isOpenedAccordion}
                      hasHover={!isOpenedAccordion}
                      hoverMode={isOpenedAccordion ? 'opacity' : 'background'}
                    >
                      <DisplayTitle level="4">{name}</DisplayTitle>
                    </Accordion.Summary>
                    <Link
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.linkIcon}
                      onClick={() => sendGoal(name)}
                    >
                      <Icon28LinkCircleOutline />
                    </Link>
                  </div>
                  <Accordion.Content className={classNames(isOpenedAccordion && styles.summary__opened)}>
                    <div className={styles.content}>
                      <Paragraph className={styles.text}>
                        <FormatWikiContent>{L.t('crush_landing_store_rule')}</FormatWikiContent>
                      </Paragraph>

                      <QrCode url={link} errorText="Error" Icon={DatingIcon} size={200} />
                    </div>
                  </Accordion.Content>
                </Accordion>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
